<script lang="ts" setup>
	import { type MagicLoginResponse } from "~/utils/types";
	import { TriangleAlertIcon } from "lucide-vue-next";
	import { getHostUrl } from "@/composables/store";
	import { useUser } from "@/stores/user";
	import { getDefaultRoute } from "@/utils/defaultRoute";
	import { safeLocalReturnTo } from "@/utils/authReturn";

	type PasswordLoginTokenResponse = {
		access_token: string;
		token_type: string;
		payment_required?: boolean;
	};

	definePageMeta({
		layout: "public",
	});
	// variables
	const email = ref<string>("");
	const password = ref<string>("");
	const route = useRoute();
	const returnTo = computed(() => safeLocalReturnTo(route.query.returnTo));

	const passwordError = ref<string>("");
	const magicError = ref<string>("");

	const passwordLogin = ref<boolean>(true);
	const isLoading = ref<boolean>(false);
	const magicLogin = ref<boolean>(false);
	const success = ref<boolean>(false);

	const requestPasswordLoginToken = async function (): Promise<PasswordLoginTokenResponse> {
		const response = await fetch(`${getHostUrl()}/auth/login/token`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				email: email.value,
				password: password.value,
			}),
		});

		if (!response.ok) {
			let errorBody: any = {};
			try {
				errorBody = await response.json();
			} catch (error) {
				errorBody = {};
			}
			throw {
				status: response.status,
				message: errorBody?.error?.message || errorBody?.detail || errorBody?.message || response.statusText,
				...errorBody,
			};
		}

		return (await response.json()) as PasswordLoginTokenResponse;
	};

	const processPasswordLogin = async function () {
		isLoading.value = true;

		try {
			const loginResponse = await requestPasswordLoginToken();
			const userStore = useUser();
			userStore.token = loginResponse.access_token;
			if (loginResponse.payment_required) {
				await navigateTo("/payment-required", { external: true });
				return;
			}
			await userStore.load(true);
			await navigateTo(returnTo.value ?? getDefaultRoute(userStore.user), { external: true });
		} catch (e) {
			if (e.status === 401) {
				passwordError.value = "Incorrect email or password.";
			} else if (e.status === 429) {
				passwordError.value = "Too many login attempts. Please wait and try again.";
			} else {
				passwordError.value = "We're having trouble logging you in. Please try again.";
			}
			isLoading.value = false;
		}
	};

	const processMagicLogin = async function () {
		try {
			await input<MagicLoginResponse>("auth/magic", { email: email.value }, {}, "POST");
			success.value = true;
			magicError.value = "";
		} catch (e) {
			if (e.status === 429) {
				magicError.value = "Too many link requests. Please wait and try again.";
			} else {
				magicError.value = "We're having trouble sending a link. Please try again.";
			}
		}
	};

	const toggleLoginMode = () => {
		passwordLogin.value = !passwordLogin.value;
		magicLogin.value = !magicLogin.value;

		passwordError.value = "";
		magicError.value = "";
	};
</script>

<template>
	<div class="login">
		<div v-if="success" class="success panel border-grey shadow-large border-radius-normal">
			<div class="icon-holder border-grey">
				<icon-complete />
			</div>

			<h1>Check your email</h1>
			<p class="check-email large">
				If
				<span style="font-weight: 800">{{ email }}</span>
				matches an email in our records, then we've sent a one-time link for you to log in.
			</p>
			<Button size="large" state="red" @click="success = false">Resend email</Button>
		</div>

		<div v-else class="panel border-grey shadow-large border-radius-normal">
			<logo class="leadx-logo" />

			<div class="header">
				<h1>Sign in to your account</h1>
				<p v-if="magicLogin" class="large">We’ll send you a one-time link for you to log in.</p>
			</div>

			<InputForm v-if="passwordLogin" :action="processPasswordLogin" v-slot="{ form, submit }" id="password-form">
				<form @submit.prevent="submit" class="as-contents" novalidate>
					<InputTextField :form="form" placeholder="Email" autocomplete="on" type="email" :required="true" v-model:value="email" />
					<InputTextField :form="form" placeholder="Password" autocomplete="off" type="password" :required="true" v-model:value="password" />
					<div class="forgot-password">
						<a class="link" href="/reset-password">Forgot password?</a>
					</div>

					<div v-if="passwordError" class="error-message">
						<TriangleAlertIcon :size="16" />
						<p class="medium">{{ passwordError }}</p>
					</div>

					<Button size="large" :loading="isLoading" @click="submit" type="submit">Sign in</Button>
				</form>
			</InputForm>

			<InputForm v-if="magicLogin" :action="processMagicLogin" v-slot="{ form, processing, submit }" id="magic-form">
				<form @submit.prevent="submit" class="as-contents" novalidate>
					<InputTextField :form="form" placeholder="Email" autocomplete="on" type="email" :required="true" v-model:value="email" />

					<div v-if="magicError" class="error-message">
						<TriangleAlertIcon :size="16" />
						<p class="medium">{{ magicError }}</p>
					</div>
					<Button size="large" :loading="processing" @click="submit">Continue</Button>
				</form>
			</InputForm>

			<div class="separator" role="separator" aria-orientation="horizontal">
				<span>OR</span>
			</div>

			<Button size="large" @click="toggleLoginMode" state="grey" class="alt-buttons border-grey">{{ passwordLogin ? "Sign in with email link" : "Back to password login" }}</Button>
		</div>
	</div>
</template>

<style scoped lang="scss">
	.login {
		display: flex;
		width: 100%;
		min-height: 100vh;
		min-height: 100dvh;
		align-items: center;
		justify-content: center;
		flex-direction: column;
		gap: 30px;
		padding: 24px 16px;
		box-sizing: border-box;
		background: radial-gradient(72.29% 72.29% at 50% 0%, $red_semi 0%, rgba(246, 248, 254, 0) 100%);
		.success {
			display: flex;
			flex-direction: column;
			align-items: center;
			text-align: center;
			gap: 15px;
			.button {
				width: 220px;
			}
			.check-email {
				margin-bottom: 30px;
			}
		}
		.panel {
			overflow: hidden;
			width: 460px;
			max-width: calc(100vw - 32px);
			padding: 50px;
			display: flex;
			flex-direction: column;

			@include phone {
				width: 100%;
				padding: 32px 22px 28px;
			}

			.header {
				display: flex;
				flex-direction: column;
				gap: 4px;
			}
			.button {
				width: 100%;
				margin-top: 0px;
			}

			.leadx-logo {
				align-self: flex-start;
			}

			.error-message {
				display: flex;
				align-items: center;
				gap: 8px;
			}

			.error-message,
			.error-message p {
				color: #c8335b;
			}

			.error-message p {
				font-weight: 500;
			}

			.alt-buttons {
				transition: all 0.1s ease;

				&:hover {
					border-color: #99a5b8;
				}
			}

			.forgot-password {
				display: flex;
				justify-content: flex-start;

				:deep(.link) {
					color: #666;
					border-bottom: none;
				}
			}

			.form {
				gap: 30px;
			}
			.as-contents {
				display: contents;
			}
			.separator {
				display: flex;
				align-items: center;
				text-align: center;
				font-size: 14px;
				color: #666;

				&::before,
				&::after {
					content: "";
					flex: 1;
					border-bottom: 1px solid #ccc;
				}

				&:before {
					margin-right: 0.75em;
				}

				&::after {
					margin-left: 0.75em;
				}
			}
		}
	}
</style>
